import React from 'react';
import { format } from 'date-fns';

export type TimeFormat = '12h' | '24h';

export type BookingState = 'idle' | 'submitting' | 'error';

interface SlotListProps {
  date: Date;
  slots: string[];
  timeZone: string;
  timeFormat: TimeFormat;
  isLoading: boolean;
  selectedSlot: string | null;
  bookingState: BookingState;
  errorMessage: string | null;
  onChangeFormat: (f: TimeFormat) => void;
  onSelectSlot: (slot: string) => void;
  onConfirm: () => void;
  onCancelSelection: () => void;
}

const formatTime = (iso: string, timeFormat: TimeFormat, timeZone: string) =>
  new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: timeFormat === '12h',
  }).format(new Date(iso));

export const SlotList: React.FC<SlotListProps> = ({
  date,
  slots,
  timeZone,
  timeFormat,
  isLoading,
  selectedSlot,
  bookingState,
  errorMessage,
  onChangeFormat,
  onSelectSlot,
  onConfirm,
  onCancelSelection,
}) => {
  return (
    <div className="border-t border-medical-100 pt-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-base font-semibold text-medical-900">
          {format(date, 'EEE')} <span className="font-normal text-medical-400">{format(date, 'd MMM')}</span>
        </h4>
        <div className="inline-flex bg-medical-100 rounded-full p-0.5 text-xs font-medium">
          {(['12h', '24h'] as const).map(opt => (
            <button
              key={opt}
              type="button"
              onClick={() => onChangeFormat(opt)}
              className={`px-2.5 py-1 rounded-full transition-colors ${
                timeFormat === opt
                  ? 'bg-white text-medical-900 shadow-sm'
                  : 'text-medical-500 hover:text-medical-700'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} className="h-11 rounded-lg bg-medical-100 animate-pulse" />
          ))}
        </div>
      ) : slots.length === 0 ? (
        <p className="text-sm text-medical-500 py-8 text-center">No availability on this day.</p>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
          {slots.map(slot => {
            const isSelected = slot === selectedSlot;
            const label = formatTime(slot, timeFormat, timeZone);

            if (!isSelected) {
              return (
                <button
                  key={slot}
                  type="button"
                  onClick={() => onSelectSlot(slot)}
                  className="w-full h-11 rounded-lg border border-medical-200 text-medical-900 font-medium text-sm hover:border-primary-500 hover:text-primary-700 transition-colors"
                >
                  {label}
                </button>
              );
            }

            return (
              <div key={slot} className="flex gap-2">
                <button
                  type="button"
                  onClick={onCancelSelection}
                  disabled={bookingState === 'submitting'}
                  className="flex-1 h-11 rounded-lg bg-medical-100 text-medical-700 font-medium text-sm transition-colors disabled:opacity-50"
                >
                  {label}
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  disabled={bookingState === 'submitting'}
                  className="flex-1 h-11 rounded-lg bg-primary-600 text-white font-semibold text-sm hover:bg-primary-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {bookingState === 'submitting' ? 'Booking…' : 'Confirm'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {errorMessage && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
};
